import type { IScope } from "angular";
import { cloneDeep, isEmpty, isEqual, isNil, remove } from "lodash";

import type { IResponseWithLoading } from "App/Modules/Models";
import { HolidayFunctions } from "App/Modules/Models";
import type { IHoliday } from "App/Modules/Models/Models/IHoliday";
import type { IHolidaySchedule } from "App/Modules/Models/Models/IHolidaySchedule";
import type { IPartnersRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services/FormContainer/PatchDelta";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import moment from "moment-timezone";

export default class EditHolidaysComponentController {
    public static $inject: string[] = ["$scope", "mmPartnersRepository"];

    public original: IHolidaySchedule;

    public edited: IHolidaySchedule;

    public editHolidaysForm: Array<{
        dtStart?: moment.Moment;
        dtStop?: moment.Moment;
        id?: string;
        name: string;
        oneDayLong: boolean;
    }>;

    public runningHoliday: IHoliday;

    public endedHolidays: IHoliday[];

    public partnerId: string;

    public isNew: boolean;

    public resolve: IFormContainerResolve<IHolidaySchedule, string>;

    public editResponse: IResponseWithLoading<IHolidaySchedule>;

    constructor(private readonly $scope: IScope, private readonly partnersRepository: IPartnersRepository) {}

    public $onInit() {
        this.isNew = isEmpty(this.resolve.formData);
        this.partnerId = this.resolve.bindings;
        this.original = this.resolve.formData || ({ name: "", holidays: [] } as IHolidaySchedule);
        this.edited = cloneDeep(this.original);
        this.edited.holidays = this.edited.holidays.sort((a, b) => a.dtStart.valueOf() - b.dtStart.valueOf());
        this.runningHoliday = this.edited.holidays.find((holiday) =>
            moment().isBetween(holiday.dtStart, holiday.dtStop),
        );

        const oneDayLong = (holiday: IHoliday): boolean => holiday.dtStop.diff(holiday.dtStart, "hours") === 0;

        this.endedHolidays = this.edited.holidays.filter((holiday) => holiday.dtStop.isBefore(moment()));
        this.editHolidaysForm = this.edited.holidays
            .filter((holiday) => holiday.dtStart.isAfter(moment()))
            .map((holiday) => {
                const oneDay = oneDayLong(holiday);
                return {
                    dtStart: holiday.dtStart.clone(),
                    dtStop: oneDay ? null : holiday.dtStop.clone(),
                    id: holiday.id,
                    name: holiday.name,
                    oneDayLong: oneDay,
                };
            });
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public addHolidayItem() {
        this.editHolidaysForm.push({
            dtStart: null,
            dtStop: null,
            name: "",
            oneDayLong: true,
        });
    }

    public removeHolidayItem(holiday: IHoliday) {
        remove(this.editHolidaysForm, (hd) => isEqual(hd, holiday));
    }

    public enableEndDate(holiday: any) {
        holiday.oneDayLong = false;
    }

    public edit() {
        const editEndDate = (endDate: moment.Moment) => endDate.clone();

        this.edited.holidays = this.editHolidaysForm
            .map((holiday) => {
                return {
                    dtStart: holiday.dtStart,
                    dtStop: holiday.oneDayLong
                        ? editEndDate(holiday.dtStart)
                        : editEndDate(holiday.dtStop || holiday.dtStart),
                    id: holiday.id,
                    name: holiday.name,
                } as IHoliday;
            })
            .concat(this.runningHoliday, this.endedHolidays)
            .filter((item) => !isNil(item));

        const delta: IHolidaySchedule | IHoliday[] = PatchDelta.difference(this.original, this.edited);
        const subscription = this.isNew
            ? this.partnersRepository.createPartnerHolidays(this.partnerId, this.edited)
            : this.partnersRepository.patchPartnerHolidays(this.partnerId, delta);

        extendWithLoadingAndFail(subscription).subscribe(
            (response: IResponseWithLoading<IHolidaySchedule>) => {
                this.editResponse = response;
                if (!response.isLoading && response.data) {
                    this.edited = response.data;
                    this.resolve.options.onOk(response.data);
                }
            },
            (error: IResponseWithLoading<null>) => {
                this.editResponse = error;
            },
        );
    }

    public getFormattedRunningHoliday() {
        return HolidayFunctions.formatHolidayDates(this.runningHoliday);
    }
}
